/**
 * CouponPackPricingEditor
 *
 * Editable volume-discount tier table for coupon-pack pricing (used for the
 * global default table on AffiliatesAdminPage and for per-user overrides in the
 * users panel modal). The tier with the highest min_quantity ≤ the order
 * quantity applies.
 *
 * The component owns a local draft of the rows; `tiers` is the source it
 * resets from (on mount, when `tiers` changes, and on Cancel). On Save it
 * validates client-side and calls `onSave` with tiers sorted by min_quantity.
 */

import React, { useState } from "react";
import {
  IconButton,
  Input,
  PlusIcon,
  PrimaryButton,
  SecondaryButton,
  TrashIcon,
} from "../../design-system";
import { borders, textColors, typography } from "../../design-system/tokens";

let nextRowKey = 1;

const toDraftRows = (tiers) =>
  (Array.isArray(tiers) ? tiers : []).map((tier) => ({
    key: `tier-${nextRowKey++}`,
    min_quantity: tier?.min_quantity != null ? String(tier.min_quantity) : "",
    percent_off: tier?.percent_off != null ? String(tier.percent_off) : "",
  }));

const validateRows = (rows) => {
  const errorsByKey = {};
  const seenMinQuantities = new Map(); // min_quantity number -> first row key
  for (const row of rows) {
    const rowErrors = {};

    const minRaw = String(row.min_quantity ?? "").trim();
    const minValue = Number(minRaw);
    if (!minRaw || !Number.isInteger(minValue) || minValue < 1) {
      rowErrors.min_quantity = "Enter a whole number of 1 or more.";
    } else if (seenMinQuantities.has(minValue)) {
      rowErrors.min_quantity = "Duplicate minimum quantity.";
    } else {
      seenMinQuantities.set(minValue, row.key);
    }

    const percentRaw = String(row.percent_off ?? "").trim();
    const percentValue = Number(percentRaw);
    if (!percentRaw || Number.isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
      rowErrors.percent_off = "Enter a percentage between 0 and 100.";
    }

    if (Object.keys(rowErrors).length > 0) errorsByKey[row.key] = rowErrors;
  }
  return errorsByKey;
};

const CouponPackPricingEditor = ({
  tiers = [],
  onSave,
  onCancel,
  saving = false,
  disabled = false,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  idPrefix = "pack-pricing",
}) => {
  const [rows, setRows] = useState(() => toDraftRows(tiers));
  const [errorsByKey, setErrorsByKey] = useState({});

  // Reset the draft whenever the source tiers change (e.g. after load or save).
  // Done during render (React's "derived state from props" pattern) instead of
  // in an effect, so the inputs are never remounted after their first paint.
  const tiersSignature = JSON.stringify(tiers || []);
  const [lastTiersSignature, setLastTiersSignature] = useState(tiersSignature);
  if (lastTiersSignature !== tiersSignature) {
    setLastTiersSignature(tiersSignature);
    setRows(toDraftRows(tiers));
    setErrorsByKey({});
  }

  const isBusy = saving || disabled;

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
    setErrorsByKey((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev, [key]: { ...prev[key], [field]: undefined } };
      return next;
    });
  };

  const handleAddTier = () => {
    setRows((prev) => [...prev, { key: `tier-${nextRowKey++}`, min_quantity: "", percent_off: "" }]);
  };

  const handleRemoveRow = (key) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
    setErrorsByKey((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleCancel = () => {
    setRows(toDraftRows(tiers));
    setErrorsByKey({});
    onCancel?.();
  };

  const handleSave = () => {
    const errors = validateRows(rows);
    setErrorsByKey(errors);
    if (Object.keys(errors).length > 0) return;

    const sortedTiers = rows
      .map((row) => ({
        min_quantity: Number(String(row.min_quantity).trim()),
        percent_off: Number(String(row.percent_off).trim()),
      }))
      .sort((a, b) => a.min_quantity - b.min_quantity);
    onSave?.(sortedTiers);
  };

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className={`${typography.bodySmall} ${textColors.secondary}`}>
          No tiers yet — orders are charged full price. Add a tier to offer a volume discount.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <span className={`${typography.label} ${textColors.secondary}`}>Min quantity</span>
            <span className={`${typography.label} ${textColors.secondary}`}>Discount %</span>
            <span className="w-9" aria-hidden="true" />
          </div>
          {rows.map((row, index) => (
            <div key={row.key} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
              <Input
                id={`${idPrefix}-min-${index}`}
                type="number"
                min="1"
                step="1"
                value={row.min_quantity}
                disabled={isBusy}
                onChange={(e) => updateRow(row.key, "min_quantity", e.target.value)}
                error={errorsByKey[row.key]?.min_quantity}
                aria-label={`Tier ${index + 1} minimum quantity`}
                placeholder="e.g. 5"
              />
              <Input
                id={`${idPrefix}-percent-${index}`}
                type="number"
                min="0"
                max="100"
                value={row.percent_off}
                disabled={isBusy}
                onChange={(e) => updateRow(row.key, "percent_off", e.target.value)}
                error={errorsByKey[row.key]?.percent_off}
                aria-label={`Tier ${index + 1} percent off`}
                placeholder="e.g. 20"
              />
              <IconButton
                size="sm"
                variant="ghost"
                icon={<TrashIcon size="sm" />}
                disabled={isBusy}
                aria-label={`Remove tier ${index + 1}`}
                title="Remove tier"
                onClick={() => handleRemoveRow(row.key)}
              />
            </div>
          ))}
        </div>
      )}

      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t ${borders.glass}`}>
        <SecondaryButton type="button" size="sm" disabled={isBusy} onClick={handleAddTier}>
          <PlusIcon size="sm" />
          Add tier
        </SecondaryButton>
        <div className="flex items-center gap-2 justify-end">
          <SecondaryButton type="button" disabled={isBusy} onClick={handleCancel}>
            {cancelLabel}
          </SecondaryButton>
          <PrimaryButton type="button" loading={saving} disabled={disabled} onClick={handleSave}>
            {saveLabel}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default CouponPackPricingEditor;
